import "./styles.css";

import { Layout, Menu } from "antd";
import Sider from "antd/lib/layout/Sider";
import { useEffect, useMemo, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";

import {
  AimOutlined,
  BarChartOutlined,
  CarOutlined,
  DiffOutlined,
  DollarOutlined,
  IdcardOutlined,
  LeftCircleOutlined,
  LogoutOutlined,
  RiseOutlined,
  MoneyCollectFilled,
  PictureOutlined,
  RadarChartOutlined,
  RightCircleOutlined,
  SettingOutlined,
  SolutionOutlined,
  UsergroupAddOutlined,
  UserOutlined,
  WarningOutlined,
  PushpinOutlined,
  MailOutlined,
  ApartmentOutlined,
  GlobalOutlined
} from "@ant-design/icons";

import axiosInstance from "../../app/axios";
import { useAppDispatch } from "../../app/hooks";
import { removeCurrentAdminValue } from "../../features/admin/currentAdminSlice";
import { removeAuth } from "../../features/auth/authSlice";
import { updateActiveProviderListValue } from "../../features/provider/activeProviderListSlice";
import NewDomainModal from "../../shared/components/modals/newDomainModal";
import {
  AdminTypeId,
  OperatorTypeId,
  SuperAdminTypeId,
} from "../../shared/constants/Resources";
import { Routes } from "../../shared/constants/Routes";
import { WebRoutes } from "../../shared/constants/WebRoutes";
import {
  selectCurrentAdminAirportQrAllowed,
  selectCurrentAdminGuestTripsEnabled,
  selectCurrentAdminPartner,
  selectCurrentAdminType,
} from "../../utils/helpers/selectorHelper";
import GroupTitleMenu from "./groupTitleMenu";

import type { MenuProps } from "antd";
const oldDomain = process.env.REACT_APP_OLD_DOMAIN;

export const PanelLayout = () => {
  const dispatch = useAppDispatch();
  const [collapsed, setCollapsed] = useState(false);
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const navigate = useNavigate();

  const signOut = async () => {
    dispatch(removeAuth());
    dispatch(removeCurrentAdminValue());
    navigate(WebRoutes.LoginRoute, { replace: true });
  };

  axiosInstance.interceptors.response.use(
    (response) => {
      return response;
    },
    (error) => {
      if (error?.response?.status === 401) {
        signOut();
      }
    },
  );

  const onOpenChange = (keys: string[]) => {
    setOpenKeys(keys);
  };

  useEffect(() => {
    if (!window.location.href.includes(WebRoutes.AssignProviderRoute)) {
      dispatch(updateActiveProviderListValue());
    }
  }, [dispatch]);

  const onClick = (e: any) => {
    switch (Number(e.key)) {
      case Routes.Dispatcher:
        return navigate(WebRoutes.TripListRoute, { replace: true });
      case Routes.DispatcherBeta:
        return navigate(WebRoutes.TripListBetaRoute, { replace: true });
      case Routes.Report:
        return navigate(WebRoutes.AdminTableRoute, { replace: true });
      case Routes.UserReport:
        return navigate(WebRoutes.UserTableRoute, { replace: true });
      case Routes.UserRequestHistory:
        return navigate(WebRoutes.TripTableRoute, { replace: true });
      case Routes.PendingUserRequestHistory:
        return navigate(WebRoutes.PendingTripListRoute, { replace: true });
      case Routes.GroupTripRequest:
        return navigate(WebRoutes.GroupTripRequest, { replace: true });
      case Routes.GroupTripPasagers:
        return navigate(WebRoutes.GroupTripPasagers, { replace: true });
      case Routes.GroupTripTrips:
        return navigate(WebRoutes.GroupTripTrips, { replace: true });
      case Routes.Banner:
        return navigate(WebRoutes.BannerRoute, { replace: true });
      case Routes.Driver:
        return navigate(WebRoutes.DriverRoute, { replace: true });
      case Routes.GroupTripsClients:
        return navigate(WebRoutes.GroupTripsClients, { replace: true });
      case Routes.MultipleLoad:
        return navigate(WebRoutes.MultipleLoad, { replace: true });
      case Routes.NotifyPassengers:
        return navigate(WebRoutes.NotifyPassenger, { replace: true });
      case Routes.IndexAdminPanel:
        return navigate(WebRoutes.IndexAdminPanel, { replace: true });
      case Routes.Departments:
        return navigate(WebRoutes.Departments, { replace: true });
      case Routes.PrivacyPolicies:
        return navigate(WebRoutes.PrivacyPolicies, { replace: true });
      case Routes.GenerateQR:
        return navigate(WebRoutes.GenerateQR, { replace: true });
      case Routes.GuestTrips:
        return navigate(WebRoutes.GuestTrips, { replace: true });
      case Routes.AirportQRList:
        return navigate(WebRoutes.AirportQRList, { replace: true });
      case Routes.PushNotifications:
        return navigate(WebRoutes.PushNotificationsRoute, { replace: true });
      case Routes.GenerateReferralQR:
        return navigate(WebRoutes.GenerateReferralQR, { replace: true });
      case Routes.Regions:
        return navigate(WebRoutes.RegionsRoute, { replace: true });
      case Routes.AdminConfiguration:
        return navigate(WebRoutes.AdminConfiguration, { replace: true });
      case Routes.RegionPrices:
        return navigate(WebRoutes.PricesRoute, { replace: true });
      case Routes.EmptyTrips:
        return navigate(WebRoutes.EmptyTrips, { replace: true });
      case Routes.Currencies:
        return navigate(WebRoutes.CurrencyRoute, { replace: true });
      case Routes.Countries:
        return navigate(WebRoutes.Countries, { replace: true });
      case Routes.ServiceTypes:
        return navigate(WebRoutes.ServiceTypeRoute, { replace: true });
      case Routes.ServiceTiers:
        return navigate(WebRoutes.ServiceTierRoute, { replace: true });
      case Routes.ReportsDashboard:
        return navigate(WebRoutes.ReportsDashboardRoute, { replace: true });
      case Routes.Shuttles:
        return navigate(WebRoutes.ShuttlesRoute, { replace: true });
      case Routes.ScrapEmails:
        return navigate(WebRoutes.ScrapEmails, { replace: true });
      case Routes.RegistrationCodes:
        return navigate(WebRoutes.RegistrationCodes, { replace: true });
      case Routes.TripParkingReport:
        return navigate(WebRoutes.TripParkingReport, { replace: true });
      case Routes.TripRequestAnalytics:
        return navigate(WebRoutes.TripRequestAnalytics, { replace: true });
      case Routes.InternationalPricing:
        return navigate(WebRoutes.InternationalPricing, { replace: true });
      case Routes.SignOut:
        return signOut();
      default:
        return "";
    }
  };

  type MenuItem = Required<MenuProps>["items"][number];

  const menuItems = useMemo(() => {
    let items: MenuItem[] = [];
    function getItem(
      label: React.ReactNode,
      key: React.Key,
      icon?: React.ReactNode,
      children?: MenuItem[],
      type?: "group",
    ): MenuItem {
      return {
        key,
        icon,
        children,
        label,
        type,
      } as MenuItem;
    }

    const adminType = selectCurrentAdminType();
    const isPartner = selectCurrentAdminPartner();
    const airportQR = selectCurrentAdminAirportQrAllowed();
    const guestTripsEnabled = selectCurrentAdminGuestTripsEnabled();

    if (adminType === SuperAdminTypeId) {
      items = [
        getItem(
          <GroupTitleMenu title="Reportes" />,
          "group5",
          undefined,
          [
            getItem("Dashboard", Routes.ReportsDashboard, <BarChartOutlined />),
            getItem("Viajes Internacionales", Routes.TripRequestAnalytics, <RiseOutlined />),
            getItem("Scrap Emails", Routes.ScrapEmails, <MailOutlined />),
            getItem("Trips Parking", Routes.TripParkingReport, <CarOutlined />),
          ],
          "group",
        ),

        getItem(
          <GroupTitleMenu title="VIAJES" />,
          "group1",
          undefined,
          [
            getItem(
              "Viajes por fecha",
              Routes.UserRequestHistory,
              <BarChartOutlined />,
            ),
            getItem(
              "Cierres",
              Routes.PendingUserRequestHistory,
              <BarChartOutlined />,
            ),
            getItem("Viajes vacíos", Routes.EmptyTrips, <IdcardOutlined />),
            getItem("Despachador", Routes.Dispatcher, <AimOutlined />),
            getItem("Despachador BETA", Routes.DispatcherBeta, <AimOutlined />),
            getItem("Carga múltiple", Routes.MultipleLoad, <DiffOutlined />),
            getItem("Shuttle", Routes.Shuttles, <PushpinOutlined />),
          ],
          "group",
        ),
        getItem(
          <GroupTitleMenu title="VIAJES EN GRUPO" />,
          "group2",
          undefined,
          [
            getItem("Clientes", Routes.GroupTripsClients, <SolutionOutlined />),
            getItem(
              "Solicitudes",
              Routes.GroupTripRequest,
              <SolutionOutlined />,
            ),
            getItem(
              "Pasajeros",
              Routes.GroupTripPasagers,
              <UsergroupAddOutlined />,
            ),
            getItem("Viajes", Routes.GroupTripTrips, <CarOutlined />),
          ],
          "group",
        ),
        getItem(
          <GroupTitleMenu title="Administración" />,
          "group3",
          undefined,
          [
            getItem("Administradores", Routes.Report, <WarningOutlined />),
            getItem("Usuarios", Routes.UserReport, <UserOutlined />),
            getItem(
              "Códigos de Registro",
              Routes.RegistrationCodes,
              <IdcardOutlined />,
            ),
            getItem("Banners", Routes.Banner, <PictureOutlined />),
            getItem("Conductores", Routes.Driver, <IdcardOutlined />),
            getItem(
              "Notificar Pasajeros",
              Routes.NotifyPassengers,
              <DiffOutlined />,
            ),
            getItem(
              "Notificaciones",
              Routes.PushNotifications,
              <AimOutlined />,
            ),
          ],
          "group",
        ),
        getItem(
          <GroupTitleMenu title="Otros" />,
          "group4",
          undefined,
          [
            getItem("Regiones", Routes.Regions, <RadarChartOutlined />),
            getItem("Precios", Routes.RegionPrices, <DollarOutlined />),
            getItem(
              "Factores internacionales",
              Routes.InternationalPricing,
              <GlobalOutlined />,
            ),
            getItem(
              "Denominaciones",
              Routes.Currencies,
              <MoneyCollectFilled />,
            ),
            getItem("Países", Routes.Countries, <GlobalOutlined />),
            getItem(
              "Tipos de Servicio",
              Routes.ServiceTypes,
              <IdcardOutlined />,
            ),
            getItem(
              "Tiers de Servicio",
              Routes.ServiceTiers,
              <ApartmentOutlined />
            ),
            getItem(
              "Politica privacidad",
              Routes.PrivacyPolicies,
              <BarChartOutlined />,
            ),
            getItem("Salir", Routes.SignOut, <LogoutOutlined />),
          ],
          "group",
        ),
      ];
    }

    if (adminType === AdminTypeId) {
      let additional: MenuItem[] = [];

      if (isPartner) {
        additional.push(
          getItem("Generar QR", Routes.GenerateQR, <BarChartOutlined />),
          getItem(
            "Configuración de Admin",
            Routes.AdminConfiguration,
            <BarChartOutlined />,
          ),
        );
      }

      if (airportQR) {
        additional.push(
          getItem("Listado de QRs", Routes.AirportQRList, <BarChartOutlined />),
        );
      }

      if (guestTripsEnabled) {
        additional.push(
          getItem("Viajes de invitado", Routes.GuestTrips, <CarOutlined />),
        );
      }

      items = [
        getItem(
          <GroupTitleMenu title="PANEL ADMIN" />,
          "group1",
          undefined,
          [getItem("Admin", Routes.IndexAdminPanel, <BarChartOutlined />)],
          "group",
        ),
        getItem(
          <GroupTitleMenu title="USUARIOS" />,
          "group2",
          undefined,
          [
            getItem("Usuarios", Routes.UserReport, <UserOutlined />),
            getItem(
              "Códigos de Registro",
              Routes.RegistrationCodes,
              <IdcardOutlined />,
            ),
            getItem("Departamentos", Routes.Departments, <UserOutlined />),
          ],
          "group",
        ),
        getItem(
          <GroupTitleMenu title="REPORTES" />,
          "group3",
          undefined,
          [
            getItem(
              "Viajes por fecha",
              Routes.UserRequestHistory,
              <BarChartOutlined />,
            ),
          ],
          "group",
        ),
        getItem(
          <GroupTitleMenu title="otros" />,
          "group4",
          undefined,
          [
            getItem(
              "Politica privacidad",
              Routes.PrivacyPolicies,
              <BarChartOutlined />,
            ),
            getItem(
              "QR de Referidos",
              Routes.GenerateReferralQR,
              <UserOutlined />,
            ),
            ...additional,
          ],
          "group",
        ),
        getItem("Salir", Routes.SignOut, <LogoutOutlined />),
      ];
    }

    if (adminType === OperatorTypeId) {
      items = [
        getItem("Despachador", Routes.Dispatcher, <AimOutlined />),
        getItem("Despachador BETA", Routes.DispatcherBeta, <AimOutlined />),
        getItem(
          "Viajes por fecha",
          Routes.UserRequestHistory,
          <BarChartOutlined />,
        ),
        getItem(
          "Cierres",
          Routes.PendingUserRequestHistory,
          <BarChartOutlined />,
        ),
        getItem("Administración", "admin", <SettingOutlined />, [
          getItem("Administradores", Routes.Report, <WarningOutlined />),
          getItem("Usuarios", Routes.UserReport, <UserOutlined />),
          getItem(
            "Códigos de Registro",
            Routes.RegistrationCodes,
            <IdcardOutlined />,
          ),
          getItem("Conductores", Routes.Driver, <IdcardOutlined />),
        ]),
        getItem("Salir", Routes.SignOut, <LogoutOutlined />),
      ];
    }
    return items;
  }, []);

  return (
    <Layout hasSider style={{ overflow: "hidden" }}>
      {oldDomain && window.location.href.includes(oldDomain) && (
        <NewDomainModal />
      )}
      <Sider trigger={null} collapsible collapsed={collapsed}>
        {collapsed ? (
          <div className="trigger">
            <RightCircleOutlined onClick={() => setCollapsed(!collapsed)} />
          </div>
        ) : (
          <div className="trigger">
            <LeftCircleOutlined onClick={() => setCollapsed(!collapsed)} />
          </div>
        )}
        <Menu
          onClick={onClick}
          mode="inline"
          items={menuItems}
          openKeys={openKeys}
          onOpenChange={onOpenChange}
          className="scroller"
        />
      </Sider>
      <Layout>
        <Outlet />
      </Layout>
    </Layout>
  );
};

export default PanelLayout;
